// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardBody, CardHeader } from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import AffiliatePrimaryButton from "../components/AffiliatePrimaryButton";
import CouponCountPicker from "../components/CouponCountPicker";
import PlanDifferenceDisclosure from "../components/PlanDifferenceDisclosure";
import { AffiliateAuthContext } from "../context/AffiliateAuthContext";
import { createCouponPackCheckout, fetchCouponPackQuote } from "../utils/affiliateApi";
import { affiliateBrandBar, affiliateBrandGradientText } from "../utils/uiClasses";

const QUOTE_DEBOUNCE_MS = 400;
const SLIDER_MAX = 200;
const COUNT_MAX = 1000;
// Mirrors PACK_COUPON_PREFIX_RE in website/utils/coupon_codes.py. The form prefills
// this with the buyer's username, so it accepts the username charset and length
// rather than the digits-only rule this field used to have.
const PREFIX_MIN_LENGTH = 5;
const PREFIX_MAX_LENGTH = 30;
const PREFIX_PATTERN = new RegExp(`^[A-Z0-9_-]{${PREFIX_MIN_LENGTH},${PREFIX_MAX_LENGTH}}$`);
const sanitizePrefix = (raw) =>
  String(raw || "").toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, PREFIX_MAX_LENGTH);

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "—";
};

const clampCount = (raw) => {
  const n = parseInt(String(raw).replace(/[^\d]/g, ""), 10);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(COUNT_MAX, n));
};

const sliderClasses =
  "w-full h-2 cursor-pointer accent-purple-700 dark:accent-pink-500";

const inputClasses = [
  "px-3 py-2 rounded-lg border",
  surfaces.primary,
  borders.default,
  typography.body,
  textColors.primary,
  "focus:outline-none focus:ring-2 focus:ring-pink-400 dark:focus:ring-pink-300",
].join(" ");

const BuyCouponsPage = () => {
  const location = useLocation();
  const { affiliateAuth } = useContext(AffiliateAuthContext) || {};
  const username = affiliateAuth?.user?.username || "";
  const [months, setMonths] = useState(1);
  const [plusCount, setPlusCount] = useState(0);
  const [proCount, setProCount] = useState(0);
  const [prefix, setPrefix] = useState("");
  // The prefix defaults to the buyer's username, but must stop tracking it the
  // moment they edit it themselves — otherwise a late session load would clobber
  // what they typed.
  const [prefixEdited, setPrefixEdited] = useState(false);

  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [isQuoting, setIsQuoting] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [isPaying, setIsPaying] = useState(false);
  const quoteRequestIdRef = useRef(0);

  // The session resolves after first render, so prefill when the username arrives.
  useEffect(() => {
    if (prefixEdited || !username) return;
    setPrefix(sanitizePrefix(username));
  }, [username, prefixEdited]);

  const paymentStatus = new URLSearchParams(location?.search || "").get("status");
  const totalCount = plusCount + proCount;
  const isPrefixValid = PREFIX_PATTERN.test(prefix);
  const canPay = totalCount >= 1 && isPrefixValid && !!quote && !isQuoting && !isPaying;

  // Live quote, debounced on months/count changes.
  useEffect(() => {
    if (totalCount < 1) {
      setQuote(null);
      setQuoteError("");
      setIsQuoting(false);
      return undefined;
    }
    setIsQuoting(true);
    const requestId = ++quoteRequestIdRef.current;
    const timer = setTimeout(async () => {
      try {
        const data = await fetchCouponPackQuote({
          months,
          plus_count: plusCount,
          pro_count: proCount,
        });
        if (quoteRequestIdRef.current !== requestId) return;
        setQuote(data);
        setQuoteError("");
      } catch (e) {
        if (quoteRequestIdRef.current !== requestId) return;
        setQuote(null);
        setQuoteError(e?.message || "Could not fetch a quote.");
      } finally {
        if (quoteRequestIdRef.current === requestId) setIsQuoting(false);
      }
    }, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [months, plusCount, proCount, totalCount]);

  const handlePay = async () => {
    if (!canPay) return;
    setCheckoutError("");
    setIsPaying(true);
    try {
      const data = await createCouponPackCheckout({
        months,
        plus_count: plusCount,
        pro_count: proCount,
        prefix,
      });
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      setCheckoutError("Could not start the payment. Please try again.");
    } catch (e) {
      setCheckoutError(e?.message || "Could not start the payment. Please try again.");
    } finally {
      setIsPaying(false);
    }
  };

  const discountPercent = Number(quote?.discount_percent || 0);

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className={`${typography.h2} ${textColors.primary}`}>Buy Coupons</h1>
          <p className={`${typography.body} ${textColors.secondary} mt-1`}>
            Purchase a pack of single-use coupons to share with your audience.
          </p>
        </div>

        {paymentStatus === "success" ? (
          <div
            role="status"
            className="rounded-xl border border-green-200 dark:border-green-800 bg-green-100 dark:bg-green-900/30 px-4 py-3"
          >
            <p className={`${typography.bodySmall} text-green-800 dark:text-green-300`}>
              Payment received — your coupons are being generated and will appear in My Coupons
              shortly.
            </p>
          </div>
        ) : null}
        {paymentStatus === "cancelled" ? (
          <div
            role="status"
            className={`rounded-xl border ${borders.default} ${surfaces.secondary} px-4 py-3`}
          >
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Payment cancelled — nothing was charged. You can adjust your pack and try again.
            </p>
          </div>
        ) : null}

        <Card>
          <CardHeader title="Configure your pack" />
          <CardBody className="space-y-6">
            {/* Months of access */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="pack-months" className={`${typography.label} ${textColors.secondary}`}>
                  Access period
                </label>
                <span className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                  {months} {months === 1 ? "month" : "months"} of access
                </span>
              </div>
              <input
                id="pack-months"
                type="range"
                min={1}
                max={60}
                step={1}
                value={months}
                onChange={(e) => setMonths(parseInt(e.target.value, 10) || 1)}
                aria-label="Months of access"
                className={sliderClasses}
              />
            </div>

            {/* Plus and Pro side by side, each with its count shown large above the bar. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CouponCountPicker
                id="plus-count"
                label="Plus coupons"
                count={plusCount}
                max={COUNT_MAX}
                sliderMax={SLIDER_MAX}
                onChange={(raw) => setPlusCount(clampCount(raw))}
              />
              <CouponCountPicker
                id="pro-count"
                label="Pro coupons"
                count={proCount}
                max={COUNT_MAX}
                sliderMax={SLIDER_MAX}
                onChange={(raw) => setProCount(clampCount(raw))}
              />
            </div>

            <PlanDifferenceDisclosure />

            {/* Coupon code prefix */}
            <div className="space-y-2">
              <label htmlFor="pack-prefix" className={`${typography.label} ${textColors.secondary}`}>
                Coupon code prefix <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                id="pack-prefix"
                type="text"
                maxLength={PREFIX_MAX_LENGTH}
                value={prefix}
                onChange={(e) => {
                  setPrefixEdited(true);
                  setPrefix(sanitizePrefix(e.target.value));
                }}
                placeholder={username ? sanitizePrefix(username) : "YOURNAME"}
                aria-describedby="pack-prefix-help"
                className={`${inputClasses} w-full sm:w-72 uppercase tracking-widest`}
              />
              <p id="pack-prefix-help" className={`${typography.caption} ${textColors.tertiary}`}>
                {PREFIX_MIN_LENGTH}–{PREFIX_MAX_LENGTH} characters (letters, digits, - or _).
                Defaults to your username. Codes are your prefix + 12 generated digits.
              </p>
              {prefix && !isPrefixValid ? (
                <p className={`${typography.caption} text-red-600 dark:text-red-400`} role="alert">
                  {prefix.length < PREFIX_MIN_LENGTH
                    ? `Use at least ${PREFIX_MIN_LENGTH} characters.`
                    : `Use at most ${PREFIX_MAX_LENGTH} characters.`}
                </p>
              ) : null}
            </div>

            {/* Live quote */}
            <div
              className={`relative overflow-hidden rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-5 shadow-sm`}
            >
              <div className={`absolute inset-x-0 top-0 h-1 ${affiliateBrandBar}`} />
              <div className="flex items-baseline justify-between gap-2">
                <h3 className={`${typography.h5} ${textColors.primary}`}>Quote</h3>
                {/* Refreshing is shown as a small note beside the heading rather than
                    by replacing the breakdown: swapping it for a one-line placeholder
                    collapsed the box and scrolled the page on every keystroke. */}
                {isQuoting && quote ? (
                  <span className={`${typography.caption} ${textColors.muted}`} role="status">
                    Updating…
                  </span>
                ) : null}
              </div>

              {totalCount < 1 ? (
                <p className={`${typography.bodySmall} ${textColors.secondary} mt-3`}>
                  Choose at least 1 coupon to see a quote.
                </p>
              ) : quoteError ? (
                <p className={`${typography.bodySmall} text-red-600 dark:text-red-400 mt-3`} role="alert">
                  {quoteError}
                </p>
              ) : !quote ? (
                <p className={`${typography.bodySmall} ${textColors.secondary} mt-3`}>
                  Updating quote…
                </p>
              ) : (
                // The previous figures stay in place while a new quote loads, dimmed
                // so they do not read as final.
                <div className={`mt-3 space-y-2 transition-opacity ${isQuoting ? "opacity-60" : ""}`}>
                  {quote.plus_count > 0 ? (
                    <div className="flex items-center justify-between">
                      <span className={`${typography.bodySmall} ${textColors.secondary}`}>
                        Plus — {formatMoney(quote.plus_unit_price)} × {quote.plus_count}
                      </span>
                      <span className={`${typography.bodySmall} ${textColors.primary}`}>
                        {formatMoney(Number(quote.plus_unit_price) * quote.plus_count)}
                      </span>
                    </div>
                  ) : null}
                  {quote.pro_count > 0 ? (
                    <div className="flex items-center justify-between">
                      <span className={`${typography.bodySmall} ${textColors.secondary}`}>
                        Pro — {formatMoney(quote.pro_unit_price)} × {quote.pro_count}
                      </span>
                      <span className={`${typography.bodySmall} ${textColors.primary}`}>
                        {formatMoney(Number(quote.pro_unit_price) * quote.pro_count)}
                      </span>
                    </div>
                  ) : null}
                  <div className={`flex items-center justify-between border-t ${borders.default} pt-2`}>
                    <span className={`${typography.bodySmall} ${textColors.secondary}`}>Subtotal</span>
                    <span className={`${typography.bodySmall} ${textColors.primary}`}>
                      {formatMoney(quote.subtotal)}
                    </span>
                  </div>
                  {discountPercent > 0 ? (
                    <div className="flex items-center justify-between">
                      <span className={`${typography.bodySmall} ${textColors.secondary}`}>
                        Volume discount — {discountPercent}% off for{" "}
                        {Number(quote.plus_count || 0) + Number(quote.pro_count || 0)}+ coupons
                      </span>
                      <span className={`${typography.bodySmall} text-green-700 dark:text-green-400`}>
                        −{formatMoney(quote.discount_amount)}
                      </span>
                    </div>
                  ) : null}
                  <p className={`${typography.caption} ${textColors.muted}`}>
                    Each coupon grants {quote.access_days} days of access.
                  </p>
                  <div className={`flex items-center justify-between border-t ${borders.default} pt-3`}>
                    <span className={`${typography.h5} ${textColors.primary}`}>Total</span>
                    <span className={`${typography.h3} ${affiliateBrandGradientText}`}>
                      {formatMoney(quote.total)} {quote.currency || "USD"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {checkoutError ? (
              <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`} role="alert">
                {checkoutError}
              </p>
            ) : null}

            <AffiliatePrimaryButton
              type="button"
              onClick={handlePay}
              disabled={!canPay}
              loading={isPaying}
              fullWidth
            >
              Proceed to payment
            </AffiliatePrimaryButton>
          </CardBody>
        </Card>

        <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-4 sm:p-5 space-y-2`}>
          <h3 className={`${typography.h5} ${textColors.primary}`}>How coupon packs work</h3>
          <div className={`space-y-2 ${typography.bodySmall} ${textColors.secondary}`}>
            <p>
              Each purchased code is a one-time-use access coupon: it gives the person who redeems
              it the chosen plan free for the access period.
            </p>
            <p>
              Coupons are generated after your payment completes and appear in My Coupons, usually
              within a few minutes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyCouponsPage;
